import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from 'src/utils/apiClient';
import NewMessageActionAppBar from './NewMessageActionAppBar';
import useApiCaller from 'src/hooks/useApiCaller';
import { TextField, Box, InputLabel, Typography, Button, Divider } from '@mui/material';
import XplorActiveNewsBodyEditor from './OneOffXplorActiveNewsBodyEditor';
import contentEditorUtils from 'src/components/common/contentEditor/utils/contentEditorUtils';
import { useSelector } from 'react-redux';
import { useMassActionState } from 'src/components/massAction/MassActionStateContext';
import ActionType from 'src/enums/actionType';
import ImageUploader from 'src/components/common/ImageUploader';
import useDerivedState from 'src/hooks/useDerivedState';
import useActionCreators from 'src/hooks/useActionCreators';
import { addDays, format, isAfter, parseISO, startOfDay } from 'date-fns';
import { getCurrentParams } from 'src/selectors/parameters/parameters';
import { loaderActions } from 'src/reducers/loader';
import { useLocation } from 'wouter';
import { useAlert } from 'src/hooks';
import massActionApi from 'src/utils/api/massActionApi';
import { DatePicker } from '@mui/x-date-pickers';
import useFetch from 'src/hooks/useFetch';
import { ProcessedFieldType } from 'src/types/types';
import VerificationCodeForm, {
  VerificationCodeFormHandle,
} from 'src/components/common/VerificationCodeForm';

interface XplorNewsConfig {
  summary: string;
  title: string;
  coverId: number | null;
  thumbnailId: number | null;
  endDate: string; // ISO date string
}

interface XplorActiveNewsEditorProps {
  isWritable: boolean;
  onCancel: () => void;
  content: string;
  fields: {
    fieldsValues: {
      contentFields: ProcessedFieldType[];
      objectFields: ProcessedFieldType[];
      linkFields: ProcessedFieldType[];
    };
    fieldsModels: Omit<ProcessedFieldType, 'id'> & { model: string }[];
  };
  initialMessage: { id: number; xplorNewsContent: string; actionTypeId: number };
  initialAction: { id?: number; actionTypeId: number; messageId?: number };
}

const XplorActiveNewsEditor = ({
  isWritable,
  onCancel,
  initialMessage = {
    id: null,
    xplorNewsContent: '{}',
    actionTypeId: ActionType.XPLORACTIVE_NEWS.id,
  },
  initialAction = { id: null, messageId: null, actionTypeId: ActionType.XPLORACTIVE_NEWS.id },
  content: initialContent,
  fields,
}: XplorActiveNewsEditorProps) => {
  const callApi = useApiCaller();
  const [, navigate] = useLocation();
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  const { setLoader } = useActionCreators({
    setLoader: loaderActions.setLoader,
  });
  const [{ currentMassActionId, currentRecipeId }] = useMassActionState();

  const currentAppLn = useSelector(({ app: { currentLanguage } }) => currentLanguage);
  const campaignId = currentMassActionId;
  const recipeId = currentRecipeId;
  const { headerStoredfileId, footerStoredfileId } = useSelector(getCurrentParams);
  const mfaActivated = useSelector(({ app: { config } }) => config.mfaActivated ?? true);

  const { data: forbiddenFields = [], status } = useFetch<string[]>({
    url: '/smartmessaging/listXplorNewsActionCoForbiddenFields',
  });

  const [message, setMessage] = useState(initialMessage);
  const [action, setAction] = useState(initialAction);

  const editorInstance = useRef<any>(null);

  const xplornewsContent = JSON.parse(message.xplorNewsContent);

  const [initialHtmlContent, setInitialHtmlContent] = useState(
    contentEditorUtils.freemarkerToHtml(initialContent ?? '', fields.fieldsModels, {
      headerFileId: headerStoredfileId,
      footerFileId: footerStoredfileId,
    })
  );

  const [xplornewsConfigValue, setConfigValue] = useDerivedState<XplorNewsConfig>(
    () => ({
      summary: xplornewsContent.summary || '',
      title: xplornewsContent.title ?? '',
      coverId: xplornewsContent.coverId || null,
      thumbnailId: xplornewsContent.thumbnailId || null,
      endDate: xplornewsContent.endDate,
    }),
    [
      xplornewsContent.coverId,
      xplornewsContent.endDate,
      xplornewsContent.summary,
      xplornewsContent.thumbnailId,
      xplornewsContent.title,
    ]
  );

  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = (name, value) => {
    if (!hasChanges) setHasChanges(true);
    setConfigValue({ ...xplornewsConfigValue, [name]: value });
  };

  const handleInputChange = event => {
    const { name, value } = event.target;
    handleChange(name, value);
  };

  const verificationFormRef = useRef<VerificationCodeFormHandle>(null);
  const show2FAVerification = useCallback(
    (codeNotificationId, targets, onConfirm) => {
      showAlert({
        title: t('smartmessaging.diffusion2FA.title'),
        customContent: <VerificationCodeForm ref={verificationFormRef} />,
        allowBackdropClose: false,
        onConfirm: async () => {
          const verificationCode = verificationFormRef.current?.getCode() ?? '';
          if (verificationCode.length !== 6) {
            verificationFormRef.current?.setError(t('smartmessaging.errorMessages.code6Digits'));
            return;
          }
          await onConfirm(targets, codeNotificationId, verificationCode);
        },
        onDismiss() {
          showAlert(null);
        },
      });
    },
    [showAlert, t]
  );

  const confirmSend = useCallback(
    async (targets, codeNotificationId = null, codeNotificationValue = null) => {
      setLoader(true);
      const success = await callApi(
        massActionApi.sendContent,
        [
          campaignId,
          null,
          ActionType.XPLORACTIVE_NEWS.id,
          targets,
          codeNotificationId,
          codeNotificationValue,
        ],
        (error, _, defaultErrorHandler) => {
          setLoader(false);
          // Handle invalid verification code error
          if (
            error?.response?.status === 400 &&
            (error?.response?.data?.message === 'api.error.code-notification.too-much-try' ||
              error?.response?.message === 'smartmessaging.massAction.error.2fa.failed')
          ) {
            showAlert({
              title: t('smartmessaging.errorMessages.warning.title'),
              msg: t(error.response.data.message),
            });
            return Promise.reject();
          }

          if (
            error?.response?.status === 400 &&
            error?.response?.data?.message.includes('api.error.code-notification')
          ) {
            verificationFormRef.current.setError(t(error.response.data.message));
            return Promise.reject();
          }
          return defaultErrorHandler();
        }
      );

      setLoader(false);

      if (success === true) {
        showAlert({
          title: t('smartmessaging.diffusionSuccess.title'),
          msg: t('smartmessaging.diffusionSuccess.message'),
        });
        navigate(`/oneoff/${campaignId}/history`);
      } else {
        showAlert({
          title: t('smartmessaging.diffusionFailure.title'),
          msg: t('smartmessaging.diffusionFailure.message'),
        });
      }
    },
    [callApi, campaignId, navigate, setLoader, showAlert, t]
  );

  const onSendContent = useCallback(async () => {
    setLoader(true);
    const { targetNumber } = await callApi(massActionApi.actionCoTargetNumber, [
      recipeId,
      null,
      ActionType.XPLORACTIVE_NEWS.id,
    ]).finally(() => setLoader(false));

    showAlert({
      title: t('smartmessaging.diffusionConfirm.title'),
      msg: `${t('smartmessaging.diffusionConfirm.message')} : ${targetNumber}`,
      onConfirm: async () => {
        if (mfaActivated) {
          // Send verification code
          setLoader(true);
          const { id: codeNotificationId } = await callApi(massActionApi.sendVerificationCode, [
            campaignId,
            ActionType.XPLORACTIVE_NEWS.id,
          ]);
          setLoader(false);

          // Show 2FA verification dialog
          show2FAVerification(codeNotificationId.split('/').pop(), targetNumber, confirmSend);
        } else {
          // 2FA disabled: send directly without verification code
          showAlert(null);
          await confirmSend(targetNumber, null, null);
        }
      },
      onDismiss() {
        showAlert(null);
      },
    });
  }, [
    setLoader,
    callApi,
    recipeId,
    showAlert,
    t,
    campaignId,
    show2FAVerification,
    confirmSend,
    mfaActivated,
  ]);

  const onSave = useCallback(async () => {
    let newContentStoredFileId = null;
    const htmlContentHasChanged = initialHtmlContent !== editorInstance.current?.getData();
    if (htmlContentHasChanged) {
      newContentStoredFileId = await callApi(
        apiClient.upload,
        [contentEditorUtils.htmlToFreemarker(null, editorInstance.current), 'text/html'],
        error => {
          showAlert({
            title: 'Failed to upload content:',
            msg: ` ${error.message}`,
            type: 'warning',
          });
          return Promise.reject(error);
        }
      );
      setInitialHtmlContent(editorInstance.current.getData());
    }

    const messageToSave = {
      ...message,
      xplorNewsContent: JSON.stringify({
        ...xplornewsConfigValue,
        contentStoredFileId: newContentStoredFileId ?? xplornewsContent.contentStoredFileId,
      }),
    };

    const savedMessage = await callApi(apiClient.insertOrUpdateMessage, [messageToSave], error =>
      // need to reject the promise to stop the flow
      Promise.reject(error)
    );
    setMessage(savedMessage);

    const actionToSave = {
      ...action,
      campaignId,
      language: currentAppLn.shortName,
      messageId: savedMessage.id,
    };
    const savedAction = await callApi(
      apiClient.insertOrUpdateCampaignAction,
      [actionToSave],
      error => Promise.reject(error)
    );
    setAction(savedAction);

    setHasChanges(false);
  }, [
    action,
    callApi,
    campaignId,
    currentAppLn.shortName,
    initialHtmlContent,
    message,
    showAlert,
    xplornewsConfigValue,
    xplornewsContent.contentStoredFileId,
  ]);

  const startDate = startOfDay(new Date());
  const endDate = xplornewsConfigValue.endDate
    ? startOfDay(parseISO(xplornewsConfigValue.endDate))
    : null;

  const endDateIsValid = endDate && isAfter(endDate, startDate);

  const titleTooLong = xplornewsConfigValue.title.length > 128;

  const titleIsValid = xplornewsConfigValue.title && !titleTooLong;

  const summaryTooLong = xplornewsConfigValue.summary.length > 256;
  const summaryIsValid = xplornewsConfigValue.summary && !summaryTooLong;

  const [bodyIsValid, setBodyIsValid] = useState(() => {
    const bodyContent = editorInstance.current?.getData()?.trim();
    return bodyContent && bodyContent.length > 0;
  });

  const formIsValid =
    !Object.values(xplornewsConfigValue).some(fieldValue => !fieldValue) &&
    titleIsValid &&
    summaryIsValid &&
    endDateIsValid;

  // useMemo is used to memoize the Editor component to avoid content to reset on re-renders (example when the formData changes or on setMessage trigger)
  const Editor = useMemo(
    () => (
      <XplorActiveNewsBodyEditor
        config={{
          readOnly: !isWritable,
          fields: fields.fieldsValues.contentFields.filter(
            field => !forbiddenFields.includes(field.id)
          ),
          height: 300,
        }}
        events={{
          loaded: event => {
            editorInstance.current = event.editor;
            setBodyIsValid(
              !!initialHtmlContent?.trim() && editorInstance.current.getData().length > 0
            );
          },
          change: () => {
            setBodyIsValid(
              editorInstance.current.getData().trim() && editorInstance.current.getData().length > 0
            );
            setHasChanges(true);
          },
        }}
        content={initialHtmlContent}
      />
    ),
    [fields.fieldsValues.contentFields, forbiddenFields, initialHtmlContent, isWritable]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <NewMessageActionAppBar
        actionId={action.id}
        actionType={action.actionTypeId}
        onSave={onSave}
        onSend={onSendContent}
        isWritable={isWritable}
        contentValidity={{ isValid: formIsValid }}
        hasChanges={hasChanges}
        currentCampaignAction={action}
        currentMassActionId={campaignId}
      />
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <Box sx={{ flex: 1, textAlign: 'left', px: 2, maxWidth: '60%', overflow: 'visible' }}>
            <Box
              sx={{
                mt: 4,
              }}
            >
              <Typography variant="h6" gutterBottom>
                {t('smartmessaging.xplorActiveNewsEditor.miniature')}
              </Typography>
              <Typography variant="body1" color="textSecondary" gutterBottom>
                {t('smartmessaging.xplorActiveNewsEditor.miniatureDescription')}
              </Typography>
              <Box>
                <InputLabel sx={{ color: 'unset' }} htmlFor="title" required>
                  {t('smartmessaging.xplorActiveNewsEditor.title')}
                </InputLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  id="title"
                  name="title"
                  value={xplornewsConfigValue.title}
                  onChange={handleInputChange}
                  error={!titleIsValid}
                  helperText={
                    !titleIsValid ? t('smartmessaging.xplorActiveNewsEditor.titleError') : ''
                  }
                />
              </Box>

              <Box
                sx={{
                  mt: 2,
                }}
              >
                <InputLabel sx={{ color: 'unset' }} htmlFor="summary" required>
                  {t('smartmessaging.xplorActiveNewsEditor.summary')}
                </InputLabel>
                <TextField
                  fullWidth
                  multiline
                  variant="outlined"
                  rows={2}
                  id="summary"
                  name="summary"
                  value={xplornewsConfigValue.summary}
                  onChange={handleInputChange}
                  error={!summaryIsValid}
                  helperText={
                    !summaryIsValid ? t('smartmessaging.xplorActiveNewsEditor.summaryError') : ''
                  }
                />
              </Box>
              <Box
                sx={{
                  mt: 2,
                }}
              >
                <InputLabel
                  sx={{ color: 'unset' }}
                  htmlFor="thumbnail"
                  error={!xplornewsConfigValue.thumbnailId}
                  required
                >
                  {t('smartmessaging.xplorActiveNewsEditor.thumbnail')}
                </InputLabel>
                <ImageUploader
                  onChange={storedFileId => {
                    handleChange('thumbnailId', storedFileId);
                  }}
                  storedFileId={xplornewsConfigValue.thumbnailId}
                />
              </Box>
              <Box
                sx={{
                  mt: 2,
                  mb: 4,
                }}
              >
                <InputLabel sx={{ color: 'unset' }} required>
                  {t('smartmessaging.xplorActiveNewsEditor.publishDates')}
                </InputLabel>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    mt: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                    }}
                  >
                    <InputLabel sx={{ color: 'unset' }} htmlFor="startDate">
                      {t('smartmessaging.xplorActiveNewsEditor.publishDates.from')}
                    </InputLabel>
                    <DatePicker
                      disabled
                      value={startDate}
                      slotProps={{
                        textField: { InputProps: { id: 'startDate' }, variant: 'outlined' },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                    }}
                  >
                    <InputLabel
                      sx={{ color: 'unset' }}
                      error={!endDateIsValid}
                      htmlFor="endDate"
                      required
                    >
                      {t('smartmessaging.xplorActiveNewsEditor.publishDates.to')}
                    </InputLabel>
                    <DatePicker
                      minDate={addDays(startDate, 1)}
                      slotProps={{
                        textField: {
                          InputProps: { id: 'endDate' },
                          variant: 'outlined',
                          error: !endDateIsValid,
                          FormHelperTextProps: { sx: { height: 0 } },
                          helperText: !endDateIsValid
                            ? t('smartmessaging.xplorActiveNewsEditor.endDateError')
                            : '',
                        },
                      }}
                      value={
                        xplornewsConfigValue.endDate ? new Date(xplornewsConfigValue.endDate) : null
                      }
                      onChange={date => {
                        handleChange('endDate', date ? format(date, 'yyyy-MM-dd') : '');
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Divider />
            <Box
              sx={{
                mt: 2.5,
              }}
            >
              <Typography variant="h6" gutterBottom>
                {t('smartmessaging.xplorActiveNewsEditor.fullContent')}
              </Typography>
              <Box>
                <InputLabel sx={{ color: 'unset' }} htmlFor="content" required error={!bodyIsValid}>
                  {t('smartmessaging.xplorActiveNewsEditor.body')}
                </InputLabel>
                {status === 'success' && Editor}
              </Box>
              <Box
                sx={{
                  mt: 1,
                }}
              >
                <InputLabel
                  sx={{ color: 'unset' }}
                  htmlFor="cover"
                  error={!xplornewsConfigValue.coverId}
                  required
                >
                  {t('smartmessaging.xplorActiveNewsEditor.coverImage')}
                </InputLabel>
                <ImageUploader
                  onChange={storedFileId => {
                    handleChange('coverId', storedFileId);
                  }}
                  storedFileId={xplornewsConfigValue.coverId}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            textAlign: 'right',
          }}
        >
          <Button variant="text" color="primary" style={{ margin: '2px' }} onClick={onCancel}>
            {t('smartmessaging.buttonLabel.previous')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default XplorActiveNewsEditor;
